import React, { useEffect } from "react";
import SimpleButton from "../../atoms/simple-button/SimpleButton";
import classes from "./MisaSupportModal.module.scss";
import {
  MisaBrand,
  MisaPriceHistoryPoint,
} from "../../pages/misa-top/MisaTypes";
import downArrow from "../../static/images/misa/down-arrow.svg";
import flatArrow from "../../static/images/misa/flat-arrow.svg";
import upArrowGreen from "../../static/images/misa/up-arrow-green.svg";
import upArrow from "../../static/images/misa/up-arrow.svg";
import sadIcon from "../../static/images/misa/sad.png";
import happyIcon from "../../static/images/misa/happy.png";
import verySadIcon from "../../static/images/misa/verysad.png";
import veryHappyIcon from "../../static/images/misa/veryhappy.png";
import cancelImage from "../../static/images/misa/cancel-image.png";
import downVector from "../../static/images/misa/down-vector.svg";
import downVectorGreen from "../../static/images/misa/down-vector-green.svg";
import pointVector from "../../static/images/misa/point-vector.svg";
import sampleStockImage from "../../static/images/misa/sample-stock.png";

type ConfirmParams = {
  transactionType: "buy" | "sell";
  indexCode: string;
  units: number;
  tradeAmount: number;
  unitPrice: number;
};

type Props = {
  isOpen: boolean;
  brand: MisaBrand | null;
  step: "overview" | "input" | "remove";
  supportPoint: number;
  availableSupportPoint: number;
  onSupportPointChange: (point: number) => void;
  onStartInput: () => void;
  onStartRemove: () => void;
  onBack: () => void;
  onClose: () => void;
  onConfirm: (params: ConfirmParams) => void;
  isSubmitting?: boolean;
};

const getTrendBadge = (trendRate = 0) => {
  if (trendRate <= -1) {
    return {
      rateText: `${trendRate.toFixed(1)}%`,
      arrowIcon: downArrow,
      weatherIcon: verySadIcon,
      className: classes.downTrend,
    };
  }

  if (trendRate < 0) {
    return {
      rateText: `${trendRate.toFixed(1)}%`,
      arrowIcon: downArrow,
      weatherIcon: sadIcon,
      className: classes.downTrend,
    };
  }

  if (trendRate === 0) {
    return {
      rateText: `${trendRate.toFixed(1)}%`,
      arrowIcon: flatArrow,
      weatherIcon: null,
      className: classes.neutralTrend,
    };
  }

  if (trendRate < 1) {
    return {
      rateText: `+${trendRate.toFixed(1)}%`,
      arrowIcon: upArrow,
      weatherIcon: happyIcon,
      className: classes.upTrend,
    };
  }

  return {
    rateText: `+${trendRate.toFixed(1)}%`,
    arrowIcon: upArrow,
    weatherIcon: veryHappyIcon,
    className: classes.upTrend,
  };
};

const formatShortDate = (date: string) => {
  const [, month, day] = date.split("-");
  return `${Number(month)}/${Number(day)}`;
};

const formatAxisLabel = (value: number) => {
  return `${Math.round(value)}`;
};

const buildChartModel = (
  history: MisaPriceHistoryPoint[],
  width: number,
  height: number,
  bottomPadding: number
) => {
  if (history.length === 0) {
    return {
      linePath: "",
      areaPath: "",
      points: [] as { x: number; y: number; price: number; date: string }[],
      horizontalGuides: [] as { y: number; label: string }[],
      latestGuideX: 0,
      plotBottomY: 0,
      latestPriceLabel: "0",
      startLabel: "",
      endLabel: "",
    };
  }

  const leftPadding = 24;
  const rightPadding = 6;
  const topPadding = 8;
  const prices = history.map((point) => point.price);
  const maxPrice = Math.max(...prices);
  const minPrice = Math.min(...prices);
  const spread = Math.max(maxPrice - minPrice, 1);
  const padding = Math.max(spread * 0.12, maxPrice * 0.02, 1);
  const axisMax = maxPrice + padding;
  const axisMin = Math.max(0, minPrice - padding);
  const midTick = axisMin + (axisMax - axisMin) / 2;
  const plotWidth = width - leftPadding - rightPadding;
  const plotHeight = height - topPadding - bottomPadding;
  const valueRange = Math.max(axisMax - axisMin, 1);
  const toY = (price: number) =>
    topPadding + ((axisMax - price) / valueRange) * plotHeight;
  const points = history.map((point, index) => {
    const x =
      leftPadding + (index / Math.max(history.length - 1, 1)) * plotWidth;
    const y = toY(point.price);
    return { x, y, price: point.price, date: point.date };
  });
  const linePath = points
    .map((point, index) => `${index === 0 ? "M" : "L"} ${point.x} ${point.y}`)
    .join(" ");
  const bottomY = topPadding + plotHeight;
  const areaPath = `${linePath} L ${points[points.length - 1].x} ${bottomY} L ${
    points[0].x
  } ${bottomY} Z`;

  return {
    linePath,
    areaPath,
    points,
    horizontalGuides: [
      { y: toY(axisMax), label: formatAxisLabel(Math.round(axisMax)) },
      { y: toY(midTick), label: formatAxisLabel(Math.round(midTick)) },
      { y: toY(axisMin), label: formatAxisLabel(Math.round(axisMin)) },
    ],
    latestGuideX: points[points.length - 1].x,
    plotBottomY: bottomY,
    latestPriceLabel: points[points.length - 1].price.toLocaleString("ja-JP"),
    startLabel: formatShortDate(history[0].date),
    endLabel: formatShortDate(history[history.length - 1].date),
  };
};

const MisaSupportModal = ({
  isOpen,
  brand,
  step,
  supportPoint,
  availableSupportPoint,
  onSupportPointChange,
  onStartInput,
  onStartRemove,
  onBack,
  onClose,
  onConfirm,
  isSubmitting = false,
}: Props) => {
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const previousOverflow = document.body.style.overflow;
    const previousOverscroll = document.body.style.overscrollBehavior;

    document.body.style.overflow = "hidden";
    document.body.style.overscrollBehavior = "none";

    return () => {
      document.body.style.overflow = previousOverflow;
      document.body.style.overscrollBehavior = previousOverscroll;
    };
  }, [isOpen]);

  if (!isOpen || brand == null) {
    return null;
  }

  const currentPoint = brand.holdingPoint ?? 0;
  const unitPrice = brand.unitPrice ?? 1000;
  const baseHoldingUnits = brand.holdingUnits ?? 0;
  const pastInvestmentPoint = brand.pastInvestmentPoint ?? 0;
  const profitLossPoint = brand.profitLossPoint ?? 0;
  const baseAverageCost =
    brand.averageCost ??
    (baseHoldingUnits > 0
      ? Math.round(pastInvestmentPoint / baseHoldingUnits)
      : 0);
  const hasInvestment = pastInvestmentPoint > 0 || baseHoldingUnits > 0;
  const maxSupportUnits = Math.floor(availableSupportPoint / unitPrice);
  const isRemoveStep = step === "remove";
  const selectedUnits = Math.min(
    Math.floor(supportPoint / unitPrice),
    isRemoveStep ? baseHoldingUnits : maxSupportUnits
  );
  const selectedSupportPoint = selectedUnits * unitPrice;
  const selectedCostAmount = Math.min(
    baseAverageCost * selectedUnits,
    pastInvestmentPoint
  );
  const remainingSupportPoint = isRemoveStep
    ? availableSupportPoint + selectedSupportPoint
    : Math.max(availableSupportPoint - selectedSupportPoint, 0);
  const displayedUnits =
    step === "overview"
      ? baseHoldingUnits
      : baseHoldingUnits + (isRemoveStep ? -selectedUnits : selectedUnits);
  const displayedInvestmentPoint =
    step === "overview"
      ? pastInvestmentPoint
      : isRemoveStep
      ? Math.max(pastInvestmentPoint - selectedCostAmount, 0)
      : pastInvestmentPoint + selectedSupportPoint;
  const displayedHoldingPoint = unitPrice * displayedUnits;
  const displayedProfitLossPoint =
    displayedHoldingPoint - displayedInvestmentPoint;
  const isConfirmEnabled = selectedUnits > 0;
  const isInputStep = step === "input" || isRemoveStep;
  const brandTrendRate = hasInvestment
    ? (brand.holdingTrendRate ?? brand.trendRate ?? 0)
    : (brand.trendRate ?? 0);
  const averagePrice =
    displayedUnits > 0
      ? isRemoveStep
        ? baseAverageCost
        : Math.round(displayedInvestmentPoint / displayedUnits)
      : null;
  const priceHistory = brand.priceHistory ?? [];
  const chart = buildChartModel(priceHistory, 224, 132, 6);
  const trendBadge = getTrendBadge(brandTrendRate);
  const unitPriceText = `${unitPrice.toLocaleString("ja-JP")}pt`;
  const representativeStockName =
    brand.representativeStockName?.trim() || "-";
  const averagePriceText =
    averagePrice == null ? "-" : averagePrice.toLocaleString("ja-JP");
  const dailyPointDelta =
    brand.holdingTrendValue ??
    Math.round(currentPoint * (brandTrendRate / 100));
  const dailyChangeArrowIcon =
    dailyPointDelta >= 0 ? upArrowGreen : downVectorGreen;
  const dailyPointText = `${dailyPointDelta >= 0 ? "+" : "-"}${Math.abs(
    dailyPointDelta
  ).toLocaleString("ja-JP")}ポイント`;
  const brandImage = brand.imageData ?? sampleStockImage;
  const brandLabel = brand.indexName ?? brand.id;

  const handleClose = () => {
    onClose();
  };

  const handleConfirm = () => {
    if (!isConfirmEnabled) {
      return;
    }
    onConfirm({
      transactionType: isRemoveStep ? "sell" : "buy",
      indexCode: brand.id,
      units: selectedUnits,
      tradeAmount: selectedSupportPoint,
      unitPrice,
    });
  };

  const handleUnitsInputChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const rawValue = event.target.value.replace(/[^\d]/g, "");
    if (rawValue === "") {
      onSupportPointChange(0);
      return;
    }

    const nextUnits = Math.min(
      Number(rawValue),
      isRemoveStep ? baseHoldingUnits : maxSupportUnits
    );
    onSupportPointChange(nextUnits * unitPrice);
  };

  return (
    <div className={classes.overlay}>
      <div className={classes.overlayInner}>
        <div
          className={`${classes.modal} ${
            isRemoveStep
              ? classes.removeModal
              : isInputStep
              ? classes.inputModal
              : classes.overviewModal
          }`}
        >
          <button type="button" className={classes.closeButton} onClick={handleClose}>
            <img src={cancelImage} alt="閉じる" />
          </button>
          {isInputStep && (
            <div className={classes.title}>
              {isRemoveStep ? "応援をやめる" : hasInvestment ? "もっと応援する！" : "応援する！"}
            </div>
          )}

          <div className={classes.brandVisual}>
            <img src={brandImage} alt={brandLabel} />
            <div className={`${classes.trendSummary} ${trendBadge.className}`}>
              {trendBadge.weatherIcon && (
                <img
                  src={trendBadge.weatherIcon}
                  alt=""
                  className={classes.trendWeatherIcon}
                />
              )}
              <div
                className={`${classes.trendTextGroup} ${
                  !trendBadge.weatherIcon ? classes.centeredTrendTextGroup : ""
                }`}
              >
                <div
                  className={`${classes.trendRateRow} ${
                    !trendBadge.weatherIcon ? classes.centeredTrendRow : ""
                  }`}
                >
                  {trendBadge.arrowIcon && (
                    <img src={trendBadge.arrowIcon} alt="" />
                  )}
                  <span>{trendBadge.rateText}</span>
                </div>
                <div
                  className={`${classes.trendLabelRow} ${
                    !trendBadge.weatherIcon ? classes.centeredTrendRow : ""
                  }`}
                >
                  <span>{unitPriceText}</span>
                </div>
              </div>
            </div>
          </div>

          {isInputStep ? (
            isRemoveStep ? (
            <>
              <div className={`${classes.priceLabel} ${classes.removePriceLabel}`}>
                あなたの推しの平均価格
              </div>
              <div className={classes.priceSummary}>
                <div className={classes.priceRow}>
                  <span className={classes.price}>{averagePriceText}</span>
                  <span className={classes.priceUnit}>/口</span>
                </div>
                <div className={classes.units}>
                  <span className={classes.unitsLabel}>保有口数</span>
                  <span className={classes.unitsValue}>{displayedUnits}</span>
                </div>
              </div>
              <div className={classes.investmentBreakdownCard}>
                <div
                  className={`${classes.investmentBreakdownTotal} ${
                    selectedUnits > 0 ? classes.highlightPointValue : ""
                  }`}
                >
                  {displayedHoldingPoint.toLocaleString("ja-JP")}pt
                </div>
                <div className={classes.investmentBreakdownTitle}>-内訳-</div>
                <div className={classes.investmentBreakdownRow}>
                  <span>運用して増えたポイント</span>
                  <span>{displayedProfitLossPoint.toLocaleString("ja-JP")}pt</span>
                </div>
                <div className={classes.investmentBreakdownRow}>
                  <span>運用しているポイント</span>
                  <span
                    className={
                      selectedUnits > 0 ? classes.highlightPointValue : ""
                    }
                  >
                    {displayedInvestmentPoint.toLocaleString("ja-JP")}pt
                  </span>
                </div>
              </div>
              <div className={`${classes.arrow} ${classes.removeArrow}`}>
                <img src={downVector} alt="" />
              </div>
              <div className={`${classes.inputRow} ${classes.removeInputRow}`}>
                <span className={classes.inputUnitPrice}>
                  {unitPrice.toLocaleString("ja-JP")}pt
                </span>
                <span className={classes.inputOperator}>×</span>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  className={classes.unitInput}
                  value={selectedUnits === 0 ? "" : `${selectedUnits}`}
                  onChange={handleUnitsInputChange}
                />
                <span className={classes.inputUnitLabel}>口をやめる</span>
              </div>
              <div className={`${classes.subLabel} ${classes.removeSubLabel}`}>
                余っているポイント
              </div>
              <div className={`${classes.availablePoint} ${classes.removeAvailablePoint}`}>
                {remainingSupportPoint.toLocaleString("ja-JP")}pt
              </div>
              <div className={`${classes.buttonGroup} ${classes.inputButtonGroup}`}>
                <SimpleButton
                  label="これで決定！"
                  width="60%"
                  color={isConfirmEnabled ? "orange" : "gray"}
                  isRadius={true}
                  disabled={!isConfirmEnabled || isSubmitting}
                  onClick={handleConfirm}
                />
                <SimpleButton
                  label="戻る"
                  width="60%"
                  color="gray"
                  isRadius={true}
                  onClick={onBack}
                />
              </div>
            </>
            ) : (
            <>
              <div className={classes.subLabel}>余っているポイント</div>
              <div className={classes.availablePoint}>
                {remainingSupportPoint.toLocaleString("ja-JP")}pt
              </div>
              <div className={classes.arrow}>
                <img src={downVector} alt="" />
              </div>
              <div className={classes.inputRow}>
                <span className={classes.inputUnitPrice}>
                  {unitPrice.toLocaleString("ja-JP")}pt
                </span>
                <span className={classes.inputOperator}>×</span>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  className={classes.unitInput}
                  value={selectedUnits === 0 ? "" : `${selectedUnits}`}
                  onChange={handleUnitsInputChange}
                />
                <span className={classes.inputUnitLabel}>口 増やす</span>
              </div>
              <div className={classes.priceLabel}>あなたの推しの平均価格</div>
              <div className={classes.priceSummary}>
                <div className={classes.priceRow}>
                  <span className={classes.price}>{averagePriceText}</span>
                  <span className={classes.priceUnit}>/口</span>
                </div>
                <div className={classes.units}>
                  <span className={classes.unitsLabel}>保有口数</span>
                  <span className={classes.unitsValue}>{displayedUnits}</span>
                </div>
              </div>
              {hasInvestment ? (
                <>
                  <div className={classes.investmentBreakdownCard}>
                    <div
                      className={`${classes.investmentBreakdownTotal} ${
                        selectedUnits > 0 ? classes.highlightPointValue : ""
                      }`}
                    >
                      {displayedHoldingPoint.toLocaleString("ja-JP")}pt
                    </div>
                    <div className={classes.investmentBreakdownTitle}>-内訳-</div>
                    <div className={classes.investmentBreakdownRow}>
                      <span>運用して増えたポイント</span>
                      <span>{displayedProfitLossPoint.toLocaleString("ja-JP")}pt</span>
                    </div>
                    <div className={classes.investmentBreakdownRow}>
                      <span>運用しているポイント</span>
                      <span
                        className={
                          selectedUnits > 0 ? classes.highlightPointValue : ""
                        }
                      >
                        {displayedInvestmentPoint.toLocaleString("ja-JP")}pt
                      </span>
                    </div>
                  </div>
                  <div className={classes.dailyChangeRow}>
                    <div className={classes.dailyChangeLabel}>前日比</div>
                    <div className={classes.dailyChangeValue}>
                      <img src={dailyChangeArrowIcon} alt="" />
                      <span>{dailyPointText}</span>
                    </div>
                  </div>
                </>
              ) : (
                <div className={classes.currentPoint}>
                  {selectedSupportPoint.toLocaleString("ja-JP")}pt
                </div>
              )}
              <div className={`${classes.buttonGroup} ${classes.inputButtonGroup}`}>
                <SimpleButton
                  label="これで決定！"
                  width="60%"
                  color={isConfirmEnabled ? "orange" : "gray"}
                  isRadius={true}
                  disabled={!isConfirmEnabled || isSubmitting}
                  onClick={handleConfirm}
                />
                <SimpleButton
                  label="戻る"
                  width="60%"
                  color="gray"
                  isRadius={true}
                  onClick={onBack}
                />
              </div>
            </>
            )
          ) : (
            <>
              <div className={classes.representativeCompanyBlock}>
                <div className={classes.representativeCompanyLabel}>
                  主なメンバー（企業）
                </div>
                <div className={classes.representativeCompanyName}>
                  {representativeStockName}
                </div>
                <div className={classes.representativeCompanySuffix}>など</div>
              </div>
              <div className={classes.chartCard}>
                <div className={classes.chartHeader}>
                  <div className={classes.chartTitle}>過去のトレンド</div>
                  <div className={classes.chartPrice}>
                    <span className={classes.chartPriceValue}>
                      <img
                        src={pointVector}
                        alt=""
                        className={classes.chartPriceIcon}
                      />
                      {chart.latestPriceLabel}pt
                    </span>
                  </div>
                </div>
                <svg
                  className={classes.chart}
                  viewBox="0 0 224 132"
                  preserveAspectRatio="none"
                  aria-label="過去30日間の価格推移"
                >
                  <defs>
                    <linearGradient id="misaChartGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#06a596" stopOpacity="0.9" />
                      <stop offset="100%" stopColor="#06a596" stopOpacity="0.05" />
                    </linearGradient>
                  </defs>
                  {chart.horizontalGuides.map((guide) => (
                    <g key={`${guide.label}-${guide.y}`}>
                      <line
                        x1="24"
                        y1={guide.y}
                        x2="224"
                        y2={guide.y}
                        className={classes.axis}
                      />
                      {guide.label && (
                        <text x="0" y={guide.y + 4} className={classes.axisLabel}>
                          {guide.label}
                        </text>
                      )}
                    </g>
                  ))}
                  <path d={chart.areaPath} fill="url(#misaChartGradient)" />
                  <path d={chart.linePath} className={classes.chartLine} />
                  {chart.points.length > 0 && (
                    <line
                      x1={chart.latestGuideX}
                      y1="8"
                      x2={chart.latestGuideX}
                      y2={chart.plotBottomY}
                      className={classes.latestGuide}
                    />
                  )}
                  {chart.points.length > 0 && (
                    <circle
                      cx={chart.points[chart.points.length - 1].x}
                      cy={chart.points[chart.points.length - 1].y}
                      r="4"
                      className={classes.chartPoint}
                    />
                  )}
                </svg>
                <div className={classes.chartFooter}>
                  <span>{chart.startLabel}</span>
                  <span>{chart.endLabel}</span>
                </div>
              </div>
              <div className={classes.priceLabel}>
                あなたの推しの平均価格
              </div>
              <div className={classes.priceSummary}>
                <div className={classes.priceRow}>
                  <span className={classes.price}>{averagePriceText}</span>
                  <span className={classes.priceUnit}>/口</span>
                </div>
                <div className={classes.units}>
                  <span className={classes.unitsLabel}>保有口数</span>
                  <span className={classes.unitsValue}>{displayedUnits}</span>
                </div>
              </div>
              {hasInvestment ? (
                <>
                  <div className={classes.investmentBreakdownCard}>
                    <div className={classes.investmentBreakdownTotal}>
                      {currentPoint.toLocaleString("ja-JP")}pt
                    </div>
                    <div className={classes.investmentBreakdownTitle}>-内訳-</div>
                    <div className={classes.investmentBreakdownRow}>
                      <span>運用して増えたポイント</span>
                      <span>{profitLossPoint.toLocaleString("ja-JP")}pt</span>
                    </div>
                    <div className={classes.investmentBreakdownRow}>
                      <span>運用しているポイント</span>
                      <span>{pastInvestmentPoint.toLocaleString("ja-JP")}pt</span>
                    </div>
                  </div>
                  <div className={classes.dailyChangeRow}>
                    <div className={classes.dailyChangeLabel}>前日比</div>
                    <div className={classes.dailyChangeValue}>
                      <img src={dailyChangeArrowIcon} alt="" />
                      <span>{dailyPointText}</span>
                    </div>
                  </div>
                  <div
                    className={`${classes.buttonGroup} ${classes.overviewButtonGroup} ${classes.investedOverviewButtonGroup}`}
                  >
                    <SimpleButton
                      label="もっと応援する！"
                      width="54%"
                      color="orange"
                      isRadius={true}
                      onClick={onStartInput}
                    />
                    <SimpleButton
                      label="応援をやめる"
                      width="54%"
                      color="orange"
                      isRadius={true}
                      onClick={onStartRemove}
                    />
                  </div>
                </>
              ) : (
                <>
                  <div className={classes.currentPoint}>
                    {currentPoint.toLocaleString("ja-JP")}pt
                  </div>
                  <div className={`${classes.buttonGroup} ${classes.overviewButtonGroup}`}>
                    <SimpleButton
                      label="応援する！"
                      width="54%"
                      color="orange"
                      isRadius={true}
                      onClick={onStartInput}
                    />
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MisaSupportModal;
